import React, { useContext, useState } from "react";
import { Alert, Table } from "reactstrap";
import RbsButton from "src/utils/RoleBasedSecurity/SecurityComponents/RbsButton";
import {
  cyclicChargesPermissionWrite,
  employeeAccountPermissionWrite,
} from "src/utils/RoleBasedSecurity/permissions";
import __ from "src/utils/Translations";
import { deleteIndividualCharge as deleteIndividualChargeApi } from "src/Pages/Company/api/rechargePlanningSystem/clients";
import { dynamicNotification } from "src/utils/Notifications";
import newRelicErrorReport from "src/utils/newRelic/newRelicErrorReport";
import OneTimeChargeModal from "src/Pages/Company/EmployeeManagement/CompanyEmployees/Edit/ChargeUps/modals/OneTimeChargeModal";
import CyclicChargeModal from "src/Pages/Company/EmployeeManagement/CompanyEmployees/Edit/ChargeUps/modals/CyclicChargeModal";
import { usePermission } from "src/utils/hooks/permissions/usePermissions";
import { CyclicChargeCandidate } from "src/Pages/Company/api/rechargePlanningSystem/rechargePlanningSystem.models";
import { ChargeUpsRow } from "src/Pages/Company/EmployeeManagement/CompanyEmployees/Edit/ChargeUps/ChargeUps.models";
import DefaultTooltip from "src/Components/Tooltips/DefaultTooltip";
import { formatToLocalDateTime, formatToLocalDate } from "src/utils/dateTime";
import { periodOptions } from "src/Pages/Company/CompanyBanks/Management/Edit/PointsChanges/CyclicCharges/utils";
import RbsContext from "src/utils/RoleBasedSecurity/RbsContext";

type ChargeUpsListProps = Readonly<{
  rows: ChargeUpsRow[];
  employeeId: string;
  onRefresh: () => void;
  onCyclicChargeCandidateUpdated: (
    pointsBankId: string,
    cyclicChargeCandidate: CyclicChargeCandidate | null
  ) => void;
}>;

const CHARGE_CAUSE_LABELS: Record<string, string> = {
  INDIVIDUAL: "Indywidualne",
  FILE: "Plik",
  RENTABLE_GROUP: "Grupa dochodowości",
  ORGANIZATION_UNIT: "Jednostka organizacyjna",
  EMPLOYEE_GROUP: "Grupa pracownicza",
  COMPANY: "Firma",
};

const SETTLEMENT_TYPE_MAPPING: Record<string, string> = {
  IN_ADVANCE: "Z góry",
  AFTERWARDS: "Z dołu",
  AFTERWARDS_ZFSS: "Z dołu ZFŚS",
  SUBSCRIPTION: "Subskrypcja",
};

const getChargeCauseLabel = (cause: string) =>
  __(CHARGE_CAUSE_LABELS[cause] ?? cause);

const PRIORITY_TOOLTIP =
  "Priorytety doładowań: 1. Indywidualne 2. Plik 3. Grupa dochodowości 4. Grupa pracownicza";

// Systemic banks that cannot be charged/discharged manually. For these the
// one-time charge button is hidden (the row still renders). Matches the bank
// machine name (`pointsBank.name`), not the display name.
const NON_CHARGEABLE_BANK_NAMES = new Set([
  "payment_zfss_general",
  "payment_bank_of_points",
]);

export default function ChargeUpsList({
  rows,
  employeeId,
  onRefresh,
  onCyclicChargeCandidateUpdated,
}: ChargeUpsListProps) {
  const [oneTimeChargeModalRow, setOneTimeChargeModalRow] =
    useState<ChargeUpsRow | null>(null);
  const [cyclicChargeModalRow, setCyclicChargeModalRow] =
    useState<ChargeUpsRow | null>(null);
  const hasCyclicChargeWritePermission = usePermission(
    cyclicChargesPermissionWrite
  );
  const hasEmployeeAccountWritePermission = usePermission(
    employeeAccountPermissionWrite
  );
  const { userInfo } = useContext(RbsContext);
  const isAhr = userInfo.isAhr();

  const deleteIndividualCharge = async (row: ChargeUpsRow) => {
    if (!row.cyclicChargeDefinitionId) {
      dynamicNotification(__("Brak skonfigurowanego doładowania cyklicznego"), "error");
      return;
    }
    try {
      const updatedCandidate = await deleteIndividualChargeApi(
        row.cyclicChargeDefinitionId,
        row.accountOwnerId,
        row.pointsBank.id
      );
      dynamicNotification(__("Usunięto doładowanie indywidualne"), "success");
      onCyclicChargeCandidateUpdated(row.pointsBank.id, updatedCandidate);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      newRelicErrorReport(e, "ChargeUps/ChargeUpsList/index.tsx");
      dynamicNotification(
        __("Usuwanie doładowania indywidualnego nie powiodło się"),
        "error"
      );
    }
  };

  return (
    <div data-t1="charge_ups" className="charge-ups-table">
      {isAhr && (
        <Alert color="warning">
          {__("Banki rozliczane z góry nie mogą być rozładowywane.")}
        </Alert>
      )}
      <Table className="mb-0">
        <thead>
          <tr>
            <th>{__("Nazwa banku")}</th>
            <th>{__("Typ rozliczenia")}</th>
            <th className="text-center">{__("Aktualne saldo")}</th>
            <th className="text-center">
              {__("Następne doładowanie")}<br/>{__("cykliczne")}
            </th>
            <th className="text-center">
              {__("Źródło doładowania")}<br/>{__("cyklicznego")}
              <DefaultTooltip
                id="chargeSourceHeader"
                content={__(PRIORITY_TOOLTIP)}
              />
            </th>
            <th className="text-center">{__("Najbliższe resetowanie")}</th>
            <th
              className="text-center sticky-right"
              style={{ width: 1, whiteSpace: "nowrap" }}
            >
              {__("Akcja")}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const { accountId, pointsBank, balance, cyclicChargeCandidate, cyclicChargeDefinitionId, reset } = row;
            const hasIndividualCyclicCharge =
              cyclicChargeCandidate?.chargeCause === "INDIVIDUAL";
            const isChargeable = !NON_CHARGEABLE_BANK_NAMES.has(
              pointsBank.name
            );

            return (
              <tr key={accountId} data-t1={pointsBank.name}>
                <td>{pointsBank.fullName}</td>
                <td>
                  {__(
                    SETTLEMENT_TYPE_MAPPING[pointsBank.settlementType] ??
                      pointsBank.settlementType
                  )}
                </td>
                <td className="text-center">
                  {Number.parseFloat(balance.toFixed(2))}
                </td>
                <td className="text-center">
                  {cyclicChargeCandidate ? (
                    <>
                      <div>{cyclicChargeCandidate.amount} pkt</div>
                      <div>
                        {formatToLocalDate(cyclicChargeCandidate.executionTime)}
                      </div>
                      <div>
                        {periodOptions.find(
                          (o) => o.value === cyclicChargeCandidate.period
                        )?.label ?? cyclicChargeCandidate.period}
                      </div>
                    </>
                  ) : (
                    "—"
                  )}
                </td>
                <td className="text-center">
                  {cyclicChargeCandidate
                    ? getChargeCauseLabel(cyclicChargeCandidate.chargeCause)
                    : "—"}
                </td>
                <td className="text-center">
                  {formatToLocalDateTime(reset?.executionTime ?? null)}
                </td>
                <td
                  className="text-center sticky-right"
                  style={{ padding: "4px 8px", width: 1 }}
                >
                  {isChargeable ? (
                    <div className="d-flex justify-content-center">
                      <RbsButton
                        data-t1="oneTimeCharge"
                        disabled={!hasEmployeeAccountWritePermission}
                        color="link"
                        onClick={() => setOneTimeChargeModalRow(row)}
                      >
                        {__("Doładuj/Rozładuj")}
                        <br />
                        {__("jednorazowo")}
                      </RbsButton>
                      <RbsButton
                        data-t1="setCyclicCharge"
                        disabled={
                          !cyclicChargeDefinitionId ||
                          !hasCyclicChargeWritePermission
                        }
                        color="link"
                        onClick={() => setCyclicChargeModalRow(row)}
                      >
                        {__("Ustaw indywidualne")}
                        <br />
                        {__("doładowanie cykliczne")}
                      </RbsButton>
                      {hasIndividualCyclicCharge && (
                        <RbsButton
                          data-t1="deleteIndividualCharge"
                          disabled={!hasCyclicChargeWritePermission}
                          color="link"
                          className="text-danger"
                          onClick={() => deleteIndividualCharge(row)}
                        >
                          {__("Usuń doładowanie")}
                          <br />
                          {__("indywidualne")}
                        </RbsButton>
                      )}
                    </div>
                  ) : (
                    "—"
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>

      {oneTimeChargeModalRow && (
        <OneTimeChargeModal
          isOpen
          account={oneTimeChargeModalRow}
          employeeId={employeeId}
          onClose={() => setOneTimeChargeModalRow(null)}
          onSuccess={onRefresh}
        />
      )}
      {cyclicChargeModalRow && (
        <CyclicChargeModal
          isOpen
          account={cyclicChargeModalRow}
          employeeId={employeeId}
          onClose={() => setCyclicChargeModalRow(null)}
          onSuccess={(cyclicChargeCandidate: CyclicChargeCandidate | null) =>
            onCyclicChargeCandidateUpdated(
              cyclicChargeModalRow.pointsBank.id,
              cyclicChargeCandidate
            )
          }
        />
      )}
    </div>
  );
}
